import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IconRefresh, IconX } from '@tabler/icons-react'
import { Tooltip } from 'react-tooltip'
import { getClassForFileType, getColorForFileType, getFileType } from './utils/TextUtils'
import { getFileDisplayName } from './utils/helpers'
import { useChatService } from './services/chatService'
import { FileProps, PortalAttachmentState } from './utils/types'
import { acquireUploadSlot } from './utils/uploadConcurrency'
import { PdfFileIcon } from './icons/Icons'

interface ChatFileProps {
  file: any
  onRemoveClick: any
  hasRemove: boolean
  size?: string //default/medium/small
  shouldUpload?: boolean
  setFileList?: (files: any) => void
  isImage?: boolean
}

export const ChatFile: React.FC<ChatFileProps> = ({
  file,
  onRemoveClick,
  hasRemove,
  size,
  shouldUpload = false,
  setFileList,
  isImage = false
}) => {
  const { uploadResourceFile } = useChatService()
  const fileType = getFileType(file.name)
  const fileName = getFileDisplayName(file.name)
  const [isHovered, setIsHovered] = useState(false)
  const hasStartedUpload = useRef(false)

  // Add keyframes for shine animation
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
    @keyframes shine {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    .shine-animation::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
      animation: shine 2s infinite;
    }
  `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  const dataTooltipId = file.name.replace(/\s/g, '')

  // Drive attachment state from the actual upload request lifecycle rather than a
  // timer: uploading -> attached (raw storage done) / failed (Spec 013).
  const runUpload = useCallback(async () => {
    if (!setFileList) return

    setFileList((prevFileList: FileProps[]) =>
      prevFileList.map((f) =>
        f.name === file.name
          ? { ...f, state: 'uploading' as PortalAttachmentState, errorMessage: undefined }
          : f
      )
    )

    const release = await acquireUploadSlot()
    try {
      const response = await uploadResourceFile(file.content)
      if (response?.status?.toLowerCase() === 'success' && response?.documentId) {
        setFileList((prevFileList: FileProps[]) =>
          prevFileList.map((f) =>
            f.name === file.name
              ? {
                  ...f,
                  id: response.documentId,
                  documentId: response.documentId,
                  ingestionStatus: response.ingestionStatus ?? null,
                  reusedExisting: response.reusedExisting ?? false,
                  state: 'attached' as PortalAttachmentState,
                  errorMessage: undefined
                }
              : f
          )
        )
      } else {
        setFileList((prevFileList: FileProps[]) =>
          prevFileList.map((f) =>
            f.name === file.name
              ? {
                  ...f,
                  state: 'failed' as PortalAttachmentState,
                  errorMessage: response?.message || 'File upload failed'
                }
              : f
          )
        )
      }
    } catch (err: any) {
      setFileList((prevFileList: FileProps[]) =>
        prevFileList.map((f) =>
          f.name === file.name
            ? {
                ...f,
                state: 'failed' as PortalAttachmentState,
                errorMessage: err?.message || 'File upload failed'
              }
            : f
        )
      )
    } finally {
      release()
    }
  }, [file.name, file.content, setFileList, uploadResourceFile])

  useEffect(() => {
    if (
      shouldUpload &&
      setFileList &&
      !file?.id &&
      file?.state !== 'failed' &&
      !hasStartedUpload.current
    ) {
      hasStartedUpload.current = true
      runUpload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = () => {
    runUpload()
  }

  const getContainerSize = (size?: string, fileType?: string) => {
    if (size && size === 'small') {
      return size
    } else if (fileType && fileType?.length > 3) {
      return 'medium'
    } else {
      return 'default'
    }
  }

  // Attachment lifecycle is only meaningful while the composer is uploading; when
  // rendering a persisted message file (shouldUpload=false) show the plain type.
  const attachmentState: PortalAttachmentState | null = shouldUpload
    ? file.state ?? (file.id ? 'attached' : 'uploading')
    : null
  const isUploading = attachmentState === 'uploading'
  const isFailed = attachmentState === 'failed'
  const isAttached = attachmentState === 'attached'
  const canRemove = !isUploading

  const subLabel = isUploading
    ? 'Uploading…'
    : isFailed
    ? 'Upload failed'
    : isAttached
    ? file.reusedExisting
      ? 'Attached · Reused copy'
      : 'Attached'
    : fileType

  // For images, render with a different layout showing the image thumbnail
  if (isImage && file?.content) {
    return (
      <div
        className={`flex rounded-xl theme-surface-muted relative overflow-hidden ${
          size === 'small' ? 'w-[56px] h-[56px]' : 'w-[56px] h-[56px]'
        }`}>
        {/* Image thumbnail as background */}
        <img src={file.content} alt={fileName} className="w-full h-full object-cover" />

        {/* Remove button overlay */}
        {hasRemove && (
          <div className="absolute top-1 right-1 theme-surface rounded-full p-0.5 shadow-sm">
            <IconX
              data-testid="removeFile"
              className={`cursor-${
                canRemove ? 'pointer' : 'not-allowed'
              } outline-none transition-transform duration-200 ease-in hover:scale-110`}
              color={canRemove ? 'var(--color-secondary)' : 'var(--text-disabled)'}
              size={16}
              onClick={() => (canRemove ? onRemoveClick(file.name, 'image') : null)}
            />
          </div>
        )}

        {/* Indeterminate indicator while the upload request is in flight */}
        {isUploading && (
          <div className="absolute bottom-0 left-0 right-0 h-[4px] overflow-hidden bg-transparent">
            <div
              data-testid="progressBar"
              className="h-full w-full bg-success relative overflow-hidden shine-animation"
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`chat-file-chip flex flex-row items-center gap-1 rounded-xl theme-surface-muted relative overflow-visible ${
        size === 'small' ? 'w-[140px] h-[35px] px-1.5' : 'w-[200px] h-[56px] px-1.5'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      {/* Icon container - 40x40px with white background */}
      <div
        className={`${
          size === 'small' ? 'w-[32px] h-[32px]' : 'w-[40px] h-[40px]'
        } chat-file-type-chip rounded flex items-center justify-center theme-surface shrink-0`}>
        {fileType === 'PDF' ? (
          <PdfFileIcon size={size === 'small' ? 16 : 20} />
        ) : (
          <span
            className={`${
              getContainerSize(size, fileType) === 'small'
                ? 'text-[9px]'
                : getContainerSize(size, fileType) === 'medium'
                ? 'text-[11px]'
                : 'caption-text'
            } font-semibold theme-text-heading`}>
            {fileType}
          </span>
        )}
      </div>

      {/* Text container with file name and type */}
      <div className="flex flex-col justify-center flex-1 min-w-0 border-l theme-border-default pl-1.5">
        <div
          className={`${
            size === 'small' ? 'text-[10px]' : 'caption-text'
          } font-semibold leading-[1.67] overflow-hidden text-ellipsis whitespace-nowrap ${
            isUploading ? 'theme-text-subheading' : 'theme-text-heading'
          }`}
          data-tooltip-id={dataTooltipId}>
          {fileName}
        </div>
        <div
          className={`${
            size === 'small' ? 'text-[9px]' : 'caption-text'
          } font-normal leading-[1.33] ${
            isFailed ? 'theme-text-danger' : 'theme-text-subheading'
          }`}>
          {subLabel}
        </div>
      </div>

      {/* Announce lifecycle transitions to assistive technology */}
      <span className="sr-only" role="status" aria-live="polite">
        {shouldUpload ? `${fileName}: ${subLabel}` : ''}
      </span>

      <Tooltip
        id={dataTooltipId}
        className="p-[10px] max-w-[250px] text-center z-[9999] rounded-md">
        {fileName}
      </Tooltip>

      {/* Retry control for a failed upload */}
      {isFailed && (
        <button
          type="button"
          data-testid="retryUpload"
          aria-label={`Retry uploading ${fileName}`}
          onClick={handleRetry}
          className="shrink-0 flex items-center justify-center rounded-full p-1 theme-text-danger hover:theme-surface outline-none">
          <IconRefresh size={16} />
        </button>
      )}

      {/* Remove button */}
      {hasRemove && isHovered && (
        <div className="absolute -top-2 -right-2 theme-surface rounded-full p-0.5 shadow-sm border theme-border-default flex items-center justify-center">
          <IconX
            data-testid="removeFile"
            className={`cursor-${
              canRemove ? 'pointer' : 'not-allowed'
            } outline-none transition-transform duration-200 ease-in hover:scale-110`}
            color={canRemove ? 'var(--color-secondary)' : 'var(--text-disabled)'}
            size={16}
            onClick={() => (canRemove ? onRemoveClick(file.name) : null)}
          />
        </div>
      )}

      {/* Indeterminate indicator while the upload request is in flight */}
      {isUploading && (
        <div className="absolute bottom-0 left-0 right-0 h-[4px] overflow-hidden rounded-b-xl bg-transparent">
          <div
            data-testid="progressBar"
            className="h-full w-full rounded-b-xl relative overflow-hidden shine-animation"
            style={{
              backgroundColor: `var(${getColorForFileType(fileType)})`
            }}
          />
        </div>
      )}
    </div>
  )
}
